// Game Constants
// All magic numbers should be defined here

// Display - Native 1080p for crisp rendering
export const DESIGN_WIDTH = 1920;
export const DESIGN_HEIGHT = 1080;

// Colors (as hex numbers for Phaser) - Tiki Glitch Island palette
export const COLORS = {
  // Tiki Glitch Island palette
  TIKI_GOLD: 0xd8a43a,        // Main UI highlights, currency, tower trim, borders
  DEEP_AMBER: 0x8a5a1e,       // Shading, wood, outlines, secondary text
  GLITCH_CYAN: 0x27d8ff,      // Tesla/lightning, magic, upgrades, special UI
  NEON_ICE: 0xa6f2ff,         // Particle glows, hover states, buffs, soft highlights
  VOLCANIC_CHARCOAL: 0x1e1b1a, // Text outlines, UI framing, shadows, enemy contrast

  // UI Colors (mapped to palette)
  PRIMARY: 0xd8a43a,          // Tiki Gold - main UI highlights, borders
  PRIMARY_LIGHT: 0xa6f2ff,    // Neon Ice Blue - hover, soft highlights
  PRIMARY_DARK: 0x8a5a1e,    // Deep Amber Shadow - shading, outlines
  SECONDARY: 0x50c878,       // Success/victory (green) - keep for game feedback
  ACCENT: 0x27d8ff,          // Glitch Cyan - special UI, upgrades
  DANGER: 0xff4444,
  WARNING: 0xffa500,

  // Background Colors (charcoal family)
  BG_DARK: 0x1e1b1a,         // Volcanic Charcoal - UI framing, shadows
  BG_MEDIUM: 0x2a2625,       // Slightly lighter charcoal
  BG_LIGHT: 0x3d3836,        // Lighter charcoal for contrast

  // Text Colors
  TEXT_WHITE: 0xffffff,
  TEXT_LIGHT: 0xa6f2ff,      // Neon Ice for soft highlights
  TEXT_DARK: 0x8a5a1e,      // Deep Amber for secondary text
  TEXT_MUTED: 0x6b4a18,     // Darker amber for muted text

  // Game Element Colors
  PLAYER_1: 0x4a90d9,
  PLAYER_2: 0x50c878,
  PLAYER_3: 0xf5a623,
  PLAYER_4: 0x9b59b6,
  TOWER_ARROW: 0x8b4513,
  TOWER_FIRE: 0xff4500,
  TOWER_ICE: 0x00bfff,
  TOWER_CANNON: 0x696969,
  MONSTER: 0xff6b6b,
  MONSTER_ELITE: 0x9b59b6,
  TREE_SLOT: 0x228b22,
  BASE: 0xdaa520,
  PATH: 0x8b7355,
};

// Hex strings for Phaser text (e.g. color: '#RRGGBB')
export const COLORS_HEX = {
  TIKI_GOLD: '#D8A43A',
  DEEP_AMBER: '#8A5A1E',
  GLITCH_CYAN: '#27D8FF',
  NEON_ICE: '#A6F2FF',
  VOLCANIC_CHARCOAL: '#1E1B1A',
  TEXT_SECONDARY: '#A67B2E',
  TEXT_MUTED: '#6B4A18',
  DANGER: '#FF4444',
};

// Game Dimensions - For 1920x1080 with 90px tiles
export const GAME = {
  // Player/Tiki
  PLAYER_WIDTH: 40,
  PLAYER_HEIGHT: 60,
  PLAYER_SPEED: 250,

  // Towers
  TOWER_SIZE: 50,
  TOWER_RANGE_ARROW: 160,
  TOWER_RANGE_FIRE: 140,
  TOWER_RANGE_ICE: 120,
  TOWER_RANGE_CANNON: 200,

  // Monsters
  MONSTER_WIDTH: 36,
  MONSTER_HEIGHT: 36,
  MONSTER_BASE_SPEED: 65,

  // Tree Slots
  TREE_SLOT_RADIUS: 32,

  // Base
  BASE_WIDTH: 100,
  BASE_HEIGHT: 75,
  STARTING_VILLAGERS: 20,

  // Map
  MAP_NODE_RADIUS: 40,
  MAP_NODE_SPACING: 150,
};

// Timing
export const TIMING = {
  WAVE_SPAWN_INTERVAL: 500, // ms between monster spawns
  WAVE_DELAY: 5000, // ms between waves
  TOWER_BUILD_TIME: 1000, // ms to build a tower
  GAME_OVER_DELAY: 2000, // ms before showing results
};

// Input
export const INPUT = {
  DEADZONE: 0.15, // Gamepad analog stick deadzone for gameplay
  MENU_DEADZONE: 0.75, // Larger deadzone for menu navigation (avoid accidental stick moves)
  REPEAT_DELAY: 400, // ms before key repeat starts
  REPEAT_RATE: 100, // ms between key repeats
};

// UI - For 1920x1080
export const UI = {
  BUTTON_WIDTH: 220,
  BUTTON_HEIGHT: 50,
  BUTTON_PADDING: 10,
  PANEL_PADDING: 20,
  FONT_SIZE_LARGE: 36,
  FONT_SIZE_MEDIUM: 24,
  FONT_SIZE_SMALL: 16,
  SLIDER_WIDTH: 250,
  SLIDER_HEIGHT: 20,
};

// Z-Index layers for 2.5D depth sorting
// Lower Y = further back, Higher Y = closer to camera (in front)
// Base layers for static elements, Y-sorted entities add their Y position
export const DEPTH = {
  BACKGROUND: 0,
  PATH: 10,
  GROUND_OBJECTS: 20,        // Shadows, ground decals
  // Y-sorted entities use 100-999 range (100 + y position normalized)
  Y_SORT_BASE: 100,          // Base for Y-sorted entities (trees, towers, monsters, players)
  Y_SORT_MAX: 999,           // Max depth for Y-sorted entities
  // Ghost layer for see-through entities (players/monsters/drops behind objects)
  GHOST_LAYER: 1000,         // Semi-transparent ghost rendering for visibility
  PROJECTILES: 1100,
  UI_BACKGROUND: 1200,
  UI_ELEMENTS: 1210,
  UI_TEXT: 1220,
  OVERLAY: 1500,
};

// Helper to calculate Y-based depth (for 2.5D sorting)
// Objects lower on screen (higher Y) render in front
export function getYSortedDepth(y: number, mapHeight: number = 1620): number {
  // Normalize Y to 0-899 range and add to base
  const normalizedY = Math.floor((y / mapHeight) * 899);
  return DEPTH.Y_SORT_BASE + normalizedY;
}

// Scene Keys
export const SCENES = {
  BOOT: 'BootScene',
  PRELOAD: 'PreloadScene',
  MENU: 'MenuScene',
  SETTINGS: 'SettingsScene',
  MAP: 'MapScene',
  GAMEPLAY: 'GameplayScene',
  RESULTS: 'ResultsScene',
  ASSET_PREVIEW: 'AssetPreviewScene',
};

// Storage Keys
export const STORAGE = {
  SETTINGS: 'monsters_settings',
  PROGRESS: 'monsters_progress',
};

// Alias for backward compatibility
export const STORAGE_KEYS = STORAGE;
